import { focusRing, Icon } from '@/design-system';
import type {
    CostKey,
    PublicDocument,
    PublicLanguage,
    PublicReport,
    PublicReportCover,
} from '@/entities/publicReport';
import { cn } from '@/shared/lib';

import type { PublicCopy } from '../copy';
import { formatDate } from '../format';
import { isModifiedClick } from '../jump';
import type { FullReportIndex } from '../reportIndex';
import type { PublicDocumentState } from '../usePublicReport';
import { chapterAnchor } from '../useReportView';
import { CostStrip } from './CostBreakdown';
import { DocumentLink, DocumentUnavailable } from './DocumentLink';

export interface ReportSummaryProps {
    report: PublicReport;
    cover: PublicReportCover;
    index: FullReportIndex;
    language: PublicLanguage;
    copy: PublicCopy;
    /** `1` op de publieke pagina; `2` in de kantoorpreview, waar de kop van de keuring `h1` is. */
    headingLevel: 1 | 2;
    /** Toont de bevindingen achter een bedrag. */
    onShowCosts: (key: CostKey) => void;
    /** Naar de hele samenvatting in het rapport; zonder: geen link. */
    onReadSummary?: () => void;
    onOpenDocument?: (document: PublicDocument) => boolean | void;
    documentState?: PublicDocumentState;
}

/**
 * De rustige samenvatting bovenaan het overzicht: welke keuring, waar, wanneer
 * en door wie, de kernzin (wat de keuring vond), de drie bedragen en de PDF.
 * Wat de koper eerst wil weten, zonder te hoeven zoeken. De hele samenvatting,
 * in de woorden van het rapport, staat een tik verder in het rapport.
 *
 * De bedragen zijn knoppen: een tik toont de bevindingen erachter. Verbergt het
 * bedrijf de kosten, dan staan ze er niet, zonder leeg vak.
 */
export function ReportSummary({
    report,
    cover,
    index,
    language,
    copy,
    headingLevel,
    onShowCosts,
    onReadSummary,
    onOpenDocument,
    documentState,
}: ReportSummaryProps) {
    const Heading = headingLevel === 1 ? 'h1' : 'h2';
    const until = formatDate(report.validUntil, language);
    const facts: Array<[string, string | null]> = [
        [copy.coverInspectedAt, formatDate(cover.inspectedAt, language)],
        [copy.coverInspector, cover.inspector ?? report.inspector?.name ?? null],
    ];

    return (
        <section className="border-b border-[var(--border-subtle)] px-[var(--pr-pad-x)] pt-7 pb-7">
            <Heading className="m-0 font-[family-name:var(--font-display)] text-[clamp(24px,4.4cqi,30px)] leading-[1.12] font-semibold tracking-[-0.025em] text-pretty text-[var(--text-primary)]">
                <span className="mb-[6px] block font-[family-name:var(--font-body)] text-[13px] leading-[1.35] font-medium tracking-normal text-[var(--text-tertiary-safe)]">
                    {report.service}
                </span>{' '}
                {report.address}
            </Heading>

            <dl className="m-0 mt-4 flex flex-wrap gap-x-8 gap-y-3">
                {facts.map(([label, value]) =>
                    value ? (
                        <div key={label} className="min-w-0">
                            <dt className="text-[12px] leading-[1.4] text-[var(--text-tertiary-safe)]">
                                {label}
                            </dt>
                            <dd className="m-0 mt-[2px] text-[14px] leading-[1.4] font-medium text-[var(--text-primary)]">
                                {value}
                            </dd>
                        </div>
                    ) : null,
                )}
            </dl>

            <p className="m-0 mt-5 max-w-[62ch] text-[16px] leading-[1.5] font-medium text-pretty text-[var(--text-primary)]">
                {copy.headline(index.counts.gebreken, index.counts.aandacht)}
            </p>

            {onReadSummary ? (
                <a
                    href={`#${chapterAnchor('samenvatting')}`}
                    onClick={(event) => {
                        if (isModifiedClick(event)) {
                            return;
                        }

                        event.preventDefault();
                        onReadSummary();
                    }}
                    className={cn(
                        '-ml-1 inline-flex min-h-[44px] items-center gap-1 rounded-[6px] px-1 text-[13px] leading-[1.3] font-medium text-[var(--accent-dark)] no-underline hover:text-[var(--accent)] hover:underline',
                        focusRing,
                    )}
                >
                    {copy.readSummary}
                    <Icon name="chevronDown" size="sm" className="-rotate-90" />
                </a>
            ) : null}

            <CostStrip
                costs={report.costs}
                index={index}
                language={language}
                copy={copy}
                onShow={onShowCosts}
                className="mt-4"
            />

            {report.documents.length > 0 ? (
                <div className="mt-6 flex flex-wrap gap-[9px]">
                    {report.documents.map((document, position) => (
                        <DocumentLink
                            key={document.key}
                            document={document}
                            onOpenDocument={onOpenDocument}
                            busy={documentState?.busy === document.key}
                            size="xl"
                            variant={position === 0 ? 'primary' : 'outline'}
                            copy={copy}
                        />
                    ))}
                </div>
            ) : null}

            {report.documents.some((document) => document.key === documentState?.failed) ? (
                <DocumentUnavailable copy={copy} />
            ) : null}

            {until ? (
                <p className="mt-3 mb-0 text-[12px] leading-[1.45] text-[var(--text-tertiary-safe)]">
                    {copy.validUntil(until)}
                </p>
            ) : null}
        </section>
    );
}
